import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import { expectedAnswer } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";
import { dealPlates, RandInt } from "./plateGame.ts";

/*
 * Math Facts Football (ADR 0103): a drive down the field where every play is a
 * fact. The answer and three near misses are the numbers on four receivers'
 * jerseys; the right one catches the ball for as many yards as that receiver
 * has run.
 *
 * Pure, like soccerGame.ts - no timers, no DOM, no React. The component feeds
 * `advance` animation-frame deltas and `throwTo` taps. Randomness (the order
 * of the numbers across the receivers, and which near misses are used) comes
 * in as `randInt`, inclusive at both ends like `getRandomInt`, so tests can fix
 * it. The numbers are Plate Smash's: `dealPlates` is `distractorsFor` plus the
 * shuffle, and is not repeated here (0103 header).
 *
 * Field position counts yards from the player's own goal line, 0 to 100: the
 * drive starts on 25, and 100 is the end zone ahead.
 */

export type Defense = "rookie" | "pro" | "allPro";

export const DEFENSES: Defense[] = ["rookie", "pro", "allPro"];

/** How long the quarterback has before the rush arrives (§4). */
export const RUSH_MS: Record<Defense, number> = {
    rookie: 10_000,
    pro: 7_000,
    allPro: 5_000,
};

/** One receiver's route: how fast it runs, and how deep it stops (§2). */
export interface Route {
    /** Yards a second. */
    speed: number;
    /** Yards past the line of scrimmage at which the route ends. */
    cap: number;
}

/** Left to right, shortest to deepest. Which one holds the answer is random. */
export const ROUTES: Route[] = [
    { speed: 3, cap: 7 },
    { speed: 4, cap: 12 },
    { speed: 5, cap: 18 },
    { speed: 6, cap: 30 },
];

export const RECEIVER_COUNT = ROUTES.length;

/** Every receiver is already this far past the line when the play starts. */
export const START_DEPTH = 1;

export const COUNTDOWN_MS = 3000;
/** A stalled frame costs at most this much of the rush (0053 §4). */
export const MAX_FRAME_MS = 250;
/** How long the ball is in the air; the component animates it for this long. */
export const FLIGHT_MS = 500;
/** The pause after a catch, and after a play that shows the right answer or ends a drive (§1). */
export const RESULT_CATCH_MS = 1500;
export const RESULT_LONG_MS = 3000;

export const SACK_YARDS = 5;
export const DRIVES_PER_GAME = 4;
export const DRIVE_START = 25;
export const YARDS_FOR_FIRST_DOWN = 10;
export const FIELD_LENGTH = 100;
export const TOUCHDOWN_POINTS = 7;
export const DOWNS = 4;

export type Phase = "countdown" | "playing" | "result" | "over";

export type PlayKind = "catch" | "touchdown" | "incomplete" | "sack";

export interface Play {
    /** 1-based; also keys the ball-flight animation so each play replays it. */
    number: number;
    kind: PlayKind;
    /** The problem as shown, e.g. "7 x 8". */
    front: string;
    /** The correct answer, as text. */
    answer: string;
    /** The receiver (0 to 3) the ball went to; null for a sack. */
    thrownTo: number | null;
    /** The receiver who held the answer. */
    rightLane: number;
    /** Yards gained; negative for a sack, 0 for an incomplete pass. */
    yards: number;
    firstDown: boolean;
    /** Fourth down, and the yards were not there. */
    turnover: boolean;
    /** A touchdown or a turnover: the next play is a new drive. */
    driveEnded: boolean;
}

/** A fact that cost a down. `picked` is the wrong number thrown to; null for a sack. */
export interface FootballMiss {
    front: string;
    answer: string;
    picked: number | null;
}

export interface FootballState {
    phase: Phase;
    defense: Defense;
    countdownLeftMs: number;
    problem: ProblemData;
    /** The number on each receiver's jersey, left to right. */
    numbers: number[];
    /** Time since the snap, capped at the rush. Frozen through the result. */
    playMs: number;
    resultLeftMs: number;

    /** 1-based. */
    drive: number;
    down: number;
    /** Where the ball is, in yards from the own goal line. */
    ball: number;
    lineToGain: number;
    /** Where the ball was and where the line to gain was at the snap: the field is drawn from these. */
    snapBall: number;
    snapLineToGain: number;

    points: number;
    touchdowns: number;
    completions: number;
    attempts: number;
    wrongThrows: number;
    sacks: number;
    misses: FootballMiss[];
    /** Problems caught, as shown ("7 x 8") - for the missed-facts store. */
    correctFronts: string[];

    lastPlay: Play | null;
    plays: number;
}

/** The problem as a child would read it: × and −, not the worksheet's x and -. */
export function prettyFront(front: string): string {
    return front.replace(/ x /g, " × ").replace(/ - /g, " − ");
}

export function startGame(defense: Defense, queue: QuizQueue, randInt: RandInt): FootballState {
    const problem = queue.next();
    return {
        phase: "countdown",
        defense,
        countdownLeftMs: COUNTDOWN_MS,
        problem,
        numbers: dealNumbers(problem, randInt),
        playMs: 0,
        resultLeftMs: 0,
        drive: 1,
        down: 1,
        ball: DRIVE_START,
        lineToGain: lineToGainFrom(DRIVE_START),
        snapBall: DRIVE_START,
        snapLineToGain: lineToGainFrom(DRIVE_START),
        points: 0,
        touchdowns: 0,
        completions: 0,
        attempts: 0,
        wrongThrows: 0,
        sacks: 0,
        misses: [],
        correctFronts: [],
        lastPlay: null,
        plays: 0,
    };
}

/** The answer and three near misses, shuffled across the four receivers (§1). */
export function dealNumbers(problem: ProblemData, randInt: RandInt): number[] {
    return dealPlates(problem, randInt).map((plate) => plate.value);
}

/** The line to gain from a spot: ten yards on, never past the goal line. */
export function lineToGainFrom(ball: number): number {
    return Math.min(FIELD_LENGTH, ball + YARDS_FOR_FIRST_DOWN);
}

/** How far downfield a receiver is, `ms` after the snap (§2). */
export function depthAt(lane: number, ms: number): number {
    const route = ROUTES[lane];
    return Math.max(START_DEPTH, Math.min(route.cap, (route.speed * ms) / 1000));
}

/** Yards to go for a first down. */
export function yardsToGo(state: Pick<FootballState, "ball" | "lineToGain">): number {
    return state.lineToGain - state.ball;
}

/** First and goal, second and goal: the line to gain is the goal line itself. */
export function isGoalToGo(state: Pick<FootballState, "lineToGain">): boolean {
    return state.lineToGain >= FIELD_LENGTH;
}

/** "Own 32" is `{ side: "own", yard: 32 }`; "Opp 18" is `{ side: "opp", yard: 18 }`. */
export function ballOn(ball: number): { side: "own" | "opp"; yard: number } {
    return ball <= FIELD_LENGTH / 2 ? { side: "own", yard: ball } : { side: "opp", yard: FIELD_LENGTH - ball };
}

/** How far the rush has closed, 0 to 1. */
export function rushProgress(state: FootballState): number {
    return Math.min(1, state.playMs / RUSH_MS[state.defense]);
}

/**
 * Settles one play: moves the ball, uses the down, and decides whether the
 * drive goes on. The situation shown through the result is the one after the
 * play; a finished drive is only reset when the next play is dealt.
 */
function settle(
    state: FootballState,
    kind: "catch" | "incomplete" | "sack",
    thrownTo: number | null,
    gain: number
): FootballState {
    const faces = cardFaces(state.problem);
    const answer = expectedAnswer(state.problem);
    const rightLane = state.numbers.indexOf(answer);

    let ball = state.ball;
    if (kind === "catch") {
        ball = Math.min(FIELD_LENGTH, ball + gain);
    } else if (kind === "sack") {
        // Never behind the one: the drive is pinned, not given away (§3).
        ball = Math.max(1, ball - SACK_YARDS);
    }

    const touchdown = ball >= FIELD_LENGTH;
    const firstDown = !touchdown && ball >= state.lineToGain;
    let down = state.down;
    let lineToGain = state.lineToGain;
    let turnover = false;
    if (!touchdown) {
        if (firstDown) {
            down = 1;
            lineToGain = lineToGainFrom(ball);
        } else {
            down += 1;
            if (down > DOWNS) {
                down = DOWNS;
                turnover = true;
            }
        }
    }
    const driveEnded = touchdown || turnover;

    const play: Play = {
        number: state.plays + 1,
        kind: touchdown ? "touchdown" : kind,
        front: faces.front,
        answer: faces.back,
        thrownTo,
        rightLane,
        yards: ball - state.ball,
        firstDown,
        turnover,
        driveEnded,
    };

    const miss: FootballMiss | null =
        kind === "catch"
            ? null
            : { front: faces.front, answer: faces.back, picked: thrownTo === null ? null : state.numbers[thrownTo] };

    return {
        ...state,
        phase: "result",
        resultLeftMs: kind === "catch" && !driveEnded ? RESULT_CATCH_MS : RESULT_LONG_MS,
        ball,
        down,
        lineToGain,
        points: state.points + (touchdown ? TOUCHDOWN_POINTS : 0),
        touchdowns: state.touchdowns + (touchdown ? 1 : 0),
        completions: state.completions + (kind === "catch" ? 1 : 0),
        attempts: state.attempts + (kind === "sack" ? 0 : 1),
        wrongThrows: state.wrongThrows + (kind === "incomplete" ? 1 : 0),
        sacks: state.sacks + (kind === "sack" ? 1 : 0),
        misses: miss ? [...state.misses, miss] : state.misses,
        correctFronts: kind === "catch" ? [...state.correctFronts, faces.front] : state.correctFronts,
        lastPlay: play,
        plays: state.plays + 1,
    };
}

/**
 * Throws to a receiver (0 to 3). Right: the play gains as many whole yards as
 * that receiver is downfield now, at least one, and no more than the goal line
 * (§1, §2). Wrong: incomplete. Ignored outside a play.
 */
export function throwTo(state: FootballState, lane: number): FootballState {
    if (state.phase !== "playing" || !Number.isInteger(lane) || lane < 0 || lane >= RECEIVER_COUNT) {
        return state;
    }
    if (state.numbers[lane] !== expectedAnswer(state.problem)) {
        return settle(state, "incomplete", lane, 0);
    }
    const gain = Math.max(1, Math.round(depthAt(lane, state.playMs)));
    return settle(state, "catch", lane, gain);
}

/**
 * Deals the next play, or ends the game after the last one. A finished drive
 * starts a new one on the 25. Draws from the queue, so the caller must not run
 * it inside a reducer (0053 §8).
 */
export function nextPlay(state: FootballState, queue: QuizQueue, randInt: RandInt): FootballState {
    if (state.phase !== "result" || !state.lastPlay) {
        return state;
    }
    const play = state.lastPlay;
    if (play.driveEnded && state.drive >= DRIVES_PER_GAME) {
        return { ...state, phase: "over", resultLeftMs: 0 };
    }

    let { drive, down, ball, lineToGain } = state;
    if (play.driveEnded) {
        drive += 1;
        down = 1;
        ball = DRIVE_START;
        lineToGain = lineToGainFrom(DRIVE_START);
    }
    const problem = queue.next();
    return {
        ...state,
        phase: "playing",
        problem,
        numbers: dealNumbers(problem, randInt),
        playMs: 0,
        resultLeftMs: 0,
        drive,
        down,
        ball,
        lineToGain,
        snapBall: ball,
        snapLineToGain: lineToGain,
    };
}

/**
 * Moves time on: the countdown, the rush, and the pause after a play. The rush
 * reaching the quarterback is a sack. Draws from the queue when the result
 * pause ends, so the caller must not run it inside a reducer.
 */
export function advance(state: FootballState, dtMs: number, queue: QuizQueue, randInt: RandInt): FootballState {
    const dt = Math.min(Math.max(dtMs, 0), MAX_FRAME_MS);
    if (dt === 0 || state.phase === "over") {
        return state;
    }
    if (state.phase === "countdown") {
        const left = state.countdownLeftMs - dt;
        return left > 0 ? { ...state, countdownLeftMs: left } : { ...state, phase: "playing", countdownLeftMs: 0 };
    }
    if (state.phase === "result") {
        const left = state.resultLeftMs - dt;
        return left > 0 ? { ...state, resultLeftMs: left } : nextPlay(state, queue, randInt);
    }

    const rush = RUSH_MS[state.defense];
    const playMs = Math.min(rush, state.playMs + dt);
    const next = { ...state, playMs };
    return playMs >= rush ? settle(next, "sack", null, 0) : next;
}
